import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Box, Container, Button, IconButton, Tabs, Tab, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    FormControl, InputLabel, Select, MenuItem, OutlinedInput, Checkbox, ListItemText, SelectChangeEvent,
    CircularProgress, Grid, Slider,
    Menu, TextField, InputAdornment, useTheme, useMediaQuery, Tooltip as MuiTooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import MenuIcon from '@mui/icons-material/Menu';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SearchIcon from '@mui/icons-material/Search';
import { useAuthContext } from '../context/AuthContext';
import { usePersistentNavigate } from '../utils/navigation';
import { UserType, Admin, Provider, Reviewer, CaseStatus, CancerType } from '../schema';
import { User } from '../getusers';
import { Case } from '../getcases';
import { getUsers } from '../queries/getusers';
import { getCases } from '../queries/getcases';
import { getDashboardMetrics } from '../queries/getdashboardmetrics';
import { deleteUser } from '../queries/deleteuser';
import { deleteCase } from '../queries/deletecase';
import { assignReviewer } from '../queries/assignreviewer';
import { updateCase } from '../queries/updatecase';
import { GetDashboardMetricsResponse } from '../getdashboardmetrics';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
    ZoomableGroup
} from 'react-simple-maps';

const PIE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];

// World map topology URL
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Helper function to transform backend location data to map format
interface LocationData {
    name: string;
    coordinates: [number, number];
    caseCount: number;
    country: string;
}

// Helper to aggregate case counts by country for choropleth coloring
const aggregateByCountry = (locations: LocationData[]): Record<string, number> => {
    const countryCounts: Record<string, number> = {};
    for (const loc of locations) {
        if (loc.country) {
            // Map common country variations to full names used in TopoJSON
            const countryMap: Record<string, string> = {
                'United States': 'United States of America',
                'USA': 'United States of America',
                'US': 'United States of America',
            };
            const normalizedCountry = countryMap[loc.country] || loc.country;
            countryCounts[normalizedCountry] = (countryCounts[normalizedCountry] || 0) + loc.caseCount;
        }
    }
    return countryCounts;
};



const AdminPortal: React.FC = () => {
    const navigate = usePersistentNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { accessToken, logout, userType, setAccessToken } = useAuthContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Initialize from URL search params
    const initialTab = (searchParams.get('tab') as 'dashboard' | 'users' | 'cases') || 'dashboard';
    const initialPage = parseInt(searchParams.get('page') || '0', 10);
    const initialRowsPerPage = parseInt(searchParams.get('rowsPerPage') || '10', 10);
    const initialUserType = parseInt(searchParams.get('userType') || UserType.PROVIDER.toString(), 10) as UserType;
    const initialHideInternal = searchParams.get('hideInternal') === 'true';

    const [view, setView] = useState<'dashboard' | 'users' | 'cases'>(initialTab);
    const [userTypeFilter, setUserTypeFilter] = useState<UserType>(initialUserType);
    const [users, setUsers] = useState<User[]>([]);
    const [reviewers, setReviewers] = useState<Reviewer[]>([]);
    const [cases, setCases] = useState<Case[]>([]);
    const [metrics, setMetrics] = useState<GetDashboardMetricsResponse | null>(null);
    const [page, setPage] = useState(initialPage);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(-1);
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(7, 'day'));
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [hideInternal, setHideInternal] = useState(initialHideInternal);
    const [hideInternalDashboard, setHideInternalDashboard] = useState(true);
    const [providerSortBy, setProviderSortBy] = useState<string>('signup_time');
    const [quickFilter, setQuickFilter] = useState<string>('last7'); // Default to Last 7 days
    const [mapZoom, setMapZoom] = useState(1); // Map zoom level (1-8)
    const [mapCenter, setMapCenter] = useState<[number, number]>([0, 20]); // Map center coordinates [lng, lat]

    // Quick filter date calculation
    const applyQuickFilter = (filterValue: string) => {
        setQuickFilter(filterValue);
        const today = dayjs();

        switch (filterValue) {
            case 'yesterday':
                setStartDate(today.subtract(1, 'day'));
                setEndDate(today.subtract(1, 'day'));
                break;
            case 'last7':
                setStartDate(today.subtract(7, 'day'));
                setEndDate(null);
                break;
            case 'last30':
                setStartDate(today.subtract(30, 'day'));
                setEndDate(null);
                break;
            case 'last90':
                setStartDate(today.subtract(90, 'day'));
                setEndDate(null);
                break;
            case 'mtd': // Month to date
                setStartDate(today.startOf('month'));
                setEndDate(null);
                break;
            case 'qtd': // Quarter to date
                const currentMonth = today.month(); // 0-11
                const quarterStartMonth = Math.floor(currentMonth / 3) * 3; // 0, 3, 6, or 9
                setStartDate(today.month(quarterStartMonth).startOf('month'));
                setEndDate(null);
                break;
            case 'ytd': // Year to date
                setStartDate(today.startOf('year'));
                setEndDate(null);
                break;
            case '':
                // No filter - clear dates
                setStartDate(null);
                setEndDate(null);
                break;
        }
    };

    const fetchUsers = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        try {
            console.log(`Fetching users with filter: ${userTypeFilter}, page: ${page}, rowsPerPage: ${rowsPerPage}, sortBy: ${providerSortBy}, hideInternal: ${hideInternal}`);
            const response = await getUsers({
                userType: userTypeFilter,
                offset: page * rowsPerPage,
                pageSize: rowsPerPage,
                sortBy: userTypeFilter === UserType.PROVIDER ? providerSortBy : undefined,
                hideInternal: userTypeFilter === UserType.PROVIDER ? hideInternal : undefined
            }, accessToken, setAccessToken, logout);
            console.log('Received users response:', response);
            setUsers(response.users);
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error("Failed to fetch users:", error);
        } finally {
            setLoading(false);
        }
    }, [accessToken, userTypeFilter, page, rowsPerPage, providerSortBy, hideInternal, setAccessToken, logout]);

    const fetchCases = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        try {
            const response = await getCases({
                offset: page * rowsPerPage,
                pageSize: rowsPerPage,
                searchQuery: appliedSearchQuery,
                hideInternal: hideInternal
            }, accessToken, setAccessToken, logout);
            setCases(response.cases);
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error("Failed to fetch cases:", error);
        } finally {
            setLoading(false);
        }
    }, [accessToken, page, rowsPerPage, appliedSearchQuery, hideInternal, setAccessToken, logout]);

    const fetchMetrics = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        try {
            const request: any = {
                hideInternal: hideInternalDashboard
            };
            if (startDate) {
                request.startDate = startDate.format('YYYY-MM-DD');
            }
            if (endDate) {
                request.endDate = endDate.format('YYYY-MM-DD');
            }
            console.log('Fetching metrics with request:', request);
            const response = await getDashboardMetrics(request, accessToken, setAccessToken, logout);
            console.log('Received metrics:', response);
            setMetrics(response);
        } catch (error) {
            console.error("Failed to fetch metrics:", error);
        } finally {
            setLoading(false);
        }
    }, [accessToken, setAccessToken, logout, startDate, endDate, hideInternalDashboard]);

    const fetchReviewers = useCallback(async () => {
        if (!accessToken) return;
        try {
            const response = await getUsers({ userType: UserType.REVIEWER, offset: 0, pageSize: 1000 }, accessToken, setAccessToken, logout);
            const reviewerList = response.users
                .map(u => u.reviewer)
                .filter((r): r is Reviewer => r !== undefined && r.isActive);
            setReviewers(reviewerList);
        } catch (error) {
            console.error("Failed to fetch reviewers:", error);
        }
    }, [accessToken, setAccessToken, logout]);

    useEffect(() => {
        if (userType !== UserType.ADMIN) {
            navigate('/');
        }
    }, [userType, navigate]);

    useEffect(() => {
        if (view === 'users') {
            fetchUsers();
        } else if (view === 'cases') {
            fetchCases();
            fetchReviewers();
        } else if (view === 'dashboard') {
            fetchMetrics();
        }
    }, [view, fetchUsers, fetchCases, fetchMetrics, fetchReviewers]);

    // Refetch metrics when dates or hideInternalDashboard change (only if on dashboard view)
    useEffect(() => {
        if (view === 'dashboard' && accessToken) {
            console.log('Filters changed, refetching metrics:', { startDate: startDate?.format('YYYY-MM-DD'), endDate: endDate?.format('YYYY-MM-DD'), hideInternalDashboard });
            fetchMetrics();
        }
    }, [startDate, endDate, hideInternalDashboard, view, accessToken, fetchMetrics]);

    // Sync state to URL search params
    useEffect(() => {
        if (window.location.pathname === '/admin') {
            const params = new URLSearchParams(searchParams);
            params.set('tab', view);
            params.set('page', page.toString());
            params.set('rowsPerPage', rowsPerPage.toString());
            params.set('userType', userTypeFilter.toString());
            params.set('hideInternal', hideInternal.toString());
            setSearchParams(params, { replace: true });
        }
    }, [view, page, rowsPerPage, userTypeFilter, hideInternal, setSearchParams]);

    const handleViewChange = (event: React.SyntheticEvent, newValue: 'dashboard' | 'users' | 'cases') => {
        setView(newValue);
        setPage(0);
        setTotalCount(-1); // Reset total count when switching views
    };

    const handleUserTypeChange = (event: SelectChangeEvent<number>) => {
        setUserTypeFilter(event.target.value as UserType);
        setPage(0);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const renderMetricTitle = (title: string, currentVal: number, goal: number | null, lowerIsBetter: boolean = false) => {
        let emoji = null;
        if (quickFilter === 'last7' && goal !== null) {
            const isMeeting = lowerIsBetter ? currentVal < goal : currentVal >= goal;
            emoji = isMeeting ? '🟢' : '🔴';
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography component="h2" variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                    {title}
                </Typography>
                {emoji && <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>{emoji}</Typography>}
            </Box>
        );
    };

    const renderGoalText = (goal: number, suffix: string = '') => {
        if (quickFilter !== 'last7') return null;
        return (
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                (Q1 goal: {goal}{suffix})
            </Typography>
        );
    };

    const renderComparison = (prevVal: number | undefined, growth: number | undefined, isCurrency: boolean = false, isTime: boolean = false, isFloat: boolean = false) => {
        if (quickFilter !== 'last7' && quickFilter !== 'last30') return null;
        if (prevVal === undefined || growth === undefined) return null;

        const periodLabel = quickFilter === 'last7' ? 'Last week' : 'Last month';
        const growthLabel = quickFilter === 'last7' ? 'w/w growth' : 'm/m growth';
        const growthColor = growth > 0 ? 'success.main' : (growth < 0 ? 'error.main' : 'text.secondary');
        const growthSign = growth > 0 ? '+' : '';

        // Format previous value: 
        // If float or currency (which effectively is float-like with $) -> use 1 decimal if requested, or default behavior
        // Actually for currency usually 2 decimals or 0.
        // User request: "any float previous period comparison metrics should be rounded to a single decimal"
        // Let's rely on isFloat flag.

        let formattedPrev = prevVal.toString();
        if (isFloat) {
            formattedPrev = prevVal.toFixed(1);
        } else if (isCurrency) {
            // For currency usually we want 2 decimals or maybe standard formatting
            // But existing code just did '$' + prevVal. 
            // If prevVal comes as float, it displays full decimals.
            // Let's leave isCurrency as is unless isFloat is also true?
            // Actually currently no metric uses 'isCurrency=true' in the code I saw earlier (Costs didn't have comparison).
            // Ah, Costs are only current values in the dashboard snippet I saw.
            // So I will only focus on isFloat.
        }

        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    {periodLabel}: {isCurrency ? '$' : ''}{formattedPrev}{isTime ? 's' : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: growthColor }}>
                    {growthLabel}: {growthSign}{growth.toFixed(1)}%
                </Typography>
            </Box>
        );
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleLogout = async () => {
        await logout();
    };

    // Top-right menu state & handlers
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);
    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(e.currentTarget);
    const handleMenuClose = () => setMenuAnchorEl(null);
    const handleMenuHome = () => {
        handleMenuClose();
        navigate('/'); // This will redirect back to admin portal due to the check in ProviderLandingPage (to be implemented)
    };
    const handleMenuLogout = async () => {
        handleMenuClose();
        await handleLogout();
    };

    const handleDeleteUser = async (userId: string, userType: UserType) => {
        const confirmDelete = window.confirm(
            'Are you sure you want to delete this user? This action cannot be undone.' +
            (userType === UserType.PROVIDER ? ' All cases submitted by this provider will also be deleted.' : '')
        );

        if (!confirmDelete || !accessToken) return;

        try {
            await deleteUser({ userId, userType }, accessToken, setAccessToken, logout);
            // Refresh the users list
            fetchUsers();
        } catch (error) {
            console.error('Failed to delete user:', error);
            alert('Failed to delete user. Please try again.');
        }
    };

    const handleDeleteCase = async (caseId: string) => {
        if (!accessToken) return;
        if (window.confirm('Are you sure you want to delete this case?')) {
            try {
                await deleteCase({ caseId }, accessToken, setAccessToken, logout);
                fetchCases();
            } catch (error) {
                console.error("Failed to delete case:", error);
                alert("Failed to delete case");
            }
        }
    };

    const handleAssignReviewer = async (caseId: string, reviewerId: string) => {
        try {
            await assignReviewer(caseId, reviewerId, accessToken, setAccessToken, logout);
            fetchCases();
        } catch (error) {
            console.error("Failed to assign reviewer:", error);
            alert("Failed to assign reviewer");
        }
    };

    const handleUpdateCancerType = async (caseId: string, newType: CancerType) => {
        if (!accessToken) return;
        try {
            await updateCase(caseId, newType, accessToken, setAccessToken, logout);
            fetchCases(); // Refresh to show updated state
        } catch (error) {
            console.error("Failed to update cancer type:", error);
            alert("Failed to update cancer type");
        }
    };

    const renderDashboard = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (!metrics) return <Typography sx={{ p: 2 }}>No metrics available.</Typography>;

        return (
            <Box sx={{ p: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => {
                                setStartDate(newValue);
                                setQuickFilter(''); // Reset quick filter when manually changing
                            }}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => {
                                setEndDate(newValue);
                                setQuickFilter(''); // Reset quick filter when manually changing
                            }}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Quick Filter</InputLabel>
                            <Select
                                value={quickFilter}
                                label="Quick Filter"
                                onChange={(e) => applyQuickFilter(e.target.value)}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                <MenuItem value="yesterday">Yesterday</MenuItem>
                                <MenuItem value="last7">Last 7 days</MenuItem>
                                <MenuItem value="last30">Last 30 days</MenuItem>
                                <MenuItem value="last90">Last 90 days</MenuItem>
                                <MenuItem value="mtd">Month to date</MenuItem>
                                <MenuItem value="qtd">Quarter to date</MenuItem>
                                <MenuItem value="ytd">Year to date</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setStartDate(null);
                                setEndDate(null);
                                setQuickFilter(''); // Reset quick filter when clearing
                            }}
                        >
                            Clear Filters
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 3 }}>
                            <Checkbox
                                checked={hideInternalDashboard}
                                onChange={(e) => setHideInternalDashboard(e.target.checked)}
                                size="small"
                            />
                            <Typography variant="body2">Exclude Internal Data</Typography>
                        </Box>
                    </Box>
                </LocalizationProvider>
                <Box sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                    {/* Key Metrics */}
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Submitted Cases", metrics.totalSubmittedCases, 300)}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography component="p" variant="h5">{metrics.totalSubmittedCases.toString()}</Typography>
                                {renderGoalText(300)}
                            </Box>
                            {renderComparison((metrics as any).previousTotalSubmittedCases, (metrics as any).totalSubmittedCasesGrowth)}
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Unique Active Providers", metrics.uniqueProvidersSubmitted, 100)}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography component="p" variant="h5">{metrics.uniqueProvidersSubmitted.toString()}</Typography>
                                {renderGoalText(100)}
                            </Box>
                            {renderComparison((metrics as any).previousUniqueProvidersSubmitted, (metrics as any).uniqueProvidersSubmittedGrowth)}
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("New Sign-ups", metrics.totalNewSignups, 50)}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography component="p" variant="h5">{metrics.totalNewSignups.toString()}</Typography>
                                {renderGoalText(50)}
                            </Box>
                            {renderComparison((metrics as any).previousTotalNewSignups, (metrics as any).totalNewSignupsGrowth)}
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Registration Completed", metrics.percentRegistrationComplete, 95)}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography component="p" variant="h5">{metrics.percentRegistrationComplete.toFixed(0)}%</Typography>
                                {renderGoalText(95, "%")}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                Completed Registrations: {(metrics as any).registrationsCompletedNumerator || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                Total Sign-ups: {(metrics as any).registrationsCompletedDenominator || 0}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Failed Cases", metrics.percentFailedCases, 2, true)}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography component="p" variant="h5">{metrics.percentFailedCases.toFixed(1)}%</Typography>
                                {renderGoalText(2, "%")}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                Failed Cases: {(metrics as any).failedCasesNumerator || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                Total Cases: {(metrics as any).failedCasesDenominator || 0}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Avg Processing Time", metrics.avgProcessingTimeSeconds, 100, true)}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography component="p" variant="h5">{metrics.avgProcessingTimeSeconds.toFixed(0)}s</Typography>
                                {renderGoalText(100, "s")}
                            </Box>
                            {renderComparison((metrics as any).previousAvgProcessingTimeSeconds, (metrics as any).avgProcessingTimeSecondsGrowth, false, true, true)}
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Survey Response Rate", metrics.percentCasesWithSurvey, null)}
                            <Typography component="p" variant="h5">{metrics.percentCasesWithSurvey.toFixed(1)}%</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                Surveys Completed: {(metrics as any).surveysCompletedNumerator || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                Total Cases: {(metrics as any).surveysCompletedDenominator || 0}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Avg Survey Score", metrics.avgSurveyResponse, 4.0)}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography component="p" variant="h5">{metrics.avgSurveyResponse.toFixed(1)}</Typography>
                                {renderGoalText(4.0)}
                            </Box>
                            {renderComparison((metrics as any).previousAvgSurveyScore, (metrics as any).avgSurveyScoreGrowth, false, false, true)}
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Comments in Surveys", metrics.percentSurveysWithComment, null)}
                            <Typography component="p" variant="h5">{metrics.percentSurveysWithComment.toFixed(0)}%</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                Surveys with Comments: {(metrics as any).surveysWithCommentsNumerator || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                Total Surveys: {(metrics as any).surveysWithCommentsDenominator || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            {renderMetricTitle("Avg Cases/Provider", metrics.avgCasesPerProvider, 3.0)}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography component="p" variant="h5">{metrics.avgCasesPerProvider.toFixed(1)}</Typography>
                                {renderGoalText(3.0)}
                            </Box>
                            {renderComparison((metrics as any).previousAvgCasesPerProvider, (metrics as any).avgCasesPerProviderGrowth, false, false, true)}
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            <Typography component="h2" variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>OpenAI API Cost*</Typography>
                            <Typography component="p" variant="h5">${metrics.openaiApiCost.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 160 }}>
                            <Typography component="h2" variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>GCP Cost*</Typography>
                            <Typography component="p" variant="h5">${metrics.gcpCost.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>

                    {/* Row 1: Cases Submitted (50%) | Map (50%) */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2, height: 380 }}>
                            <Typography component="h2" variant="h6" color="primary" gutterBottom>Cases Submitted Per Day</Typography>
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={metrics.casesPerDay}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#8884d8" name="Cases" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                    {/* World Map - Case Locations */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2, height: 380, position: 'relative' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography component="h2" variant="h6" color="primary">Case Locations</Typography>
                                {/* Zoom Controls */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setMapZoom(z => Math.max(1, z - 0.5))}
                                        disabled={mapZoom <= 1}
                                    >
                                        <RemoveIcon fontSize="small" />
                                    </IconButton>
                                    <Slider
                                        value={mapZoom}
                                        onChange={(_, value) => setMapZoom(value as number)}
                                        min={1}
                                        max={8}
                                        step={0.5}
                                        sx={{ width: 80 }}
                                        size="small"
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => setMapZoom(z => Math.min(8, z + 0.5))}
                                        disabled={mapZoom >= 8}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, minWidth: 30 }}>
                                        {mapZoom.toFixed(1)}x
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' },
                                    '& svg': { touchAction: 'pan-y' }
                                }}
                                onDoubleClick={() => setMapZoom(z => Math.min(8, z + 1))}
                                onWheel={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                <ComposableMap
                                    projection="geoMercator"
                                    projectionConfig={{
                                        scale: 130,
                                        center: [0, 30]
                                    }}
                                    style={{ width: '100%', height: 300, touchAction: 'pan-y' }}
                                >
                                    <ZoomableGroup
                                        zoom={mapZoom}
                                        center={mapCenter}
                                        onMoveEnd={({ coordinates, zoom }) => {
                                            setMapZoom(zoom);
                                            setMapCenter(coordinates);
                                        }}
                                        minZoom={1}
                                        maxZoom={8}
                                        filterZoomEvent={(evt: any) => {
                                            // Block only wheel/scroll events, allow mouse drag and touch
                                            return evt.type !== 'wheel';
                                        }}
                                    >
                                        {(() => {
                                            const locations: LocationData[] = (metrics.caseLocations || []).map((loc: any) => ({
                                                name: loc.name || 'Unknown',
                                                coordinates: [loc.longitude || 0, loc.latitude || 0] as [number, number],
                                                caseCount: loc.caseCount || 0,
                                                country: loc.country || ''
                                            }));
                                            const countryCounts = aggregateByCountry(locations);

                                            return (
                                                <>
                                                    <Geographies geography={GEO_URL}>
                                                        {({ geographies }: { geographies: any[] }) =>
                                                            geographies.map((geo: any) => {
                                                                const countryName = geo.properties.name;
                                                                const caseCount = countryCounts[countryName] || 0;
                                                                return (
                                                                    <MuiTooltip
                                                                        key={geo.rsmKey}
                                                                        title={caseCount > 0 ? `${countryName}: ${caseCount} cases` : countryName}
                                                                        arrow
                                                                        placement="top"
                                                                    >
                                                                        <Geography
                                                                            geography={geo}
                                                                            fill={caseCount > 0 ? '#B3D4FC' : '#E4E5E6'}
                                                                            stroke="#D6D6DA"
                                                                            strokeWidth={0.5}
                                                                            style={{
                                                                                default: { outline: 'none' },
                                                                                hover: { outline: 'none', fill: caseCount > 0 ? '#7AB8F5' : '#CFD8DC' },
                                                                                pressed: { outline: 'none' },
                                                                            }}
                                                                        />
                                                                    </MuiTooltip>
                                                                );
                                                            })
                                                        }
                                                    </Geographies>
                                                    {locations.map(({ name, coordinates, caseCount }: LocationData) => (
                                                        <MuiTooltip key={name} title={`${name}: ${caseCount} cases`} arrow>
                                                            <Marker coordinates={coordinates}>
                                                                <circle
                                                                    r={Math.max(3, Math.sqrt(caseCount) * 1.5)}
                                                                    fill="#4299e1"
                                                                    fillOpacity={0.8}
                                                                    stroke="#2c5282"
                                                                    strokeWidth={1}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                            </Marker>
                                                        </MuiTooltip>
                                                    ))}
                                                </>
                                            );
                                        })()}
                                    </ZoomableGroup>
                                </ComposableMap>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Row 2: Provider Signups (50%) | API Costs (50%) */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2, height: 280 }}>
                            <Typography component="h2" variant="h6" color="primary" gutterBottom>Provider Signups Per Day</Typography>
                            <ResponsiveContainer width="100%" height={220}>
                                <LineChart data={metrics.providerSignupsPerDay}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="count" stroke="#82ca9d" name="Signups" />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2, height: 280 }}>
                            <Typography component="h2" variant="h6" color="primary" gutterBottom>API Costs Per Day</Typography>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={
                                    // Use real data if available, otherwise estimate from cases
                                    ((metrics as any).apiCostsPerDay && (metrics as any).apiCostsPerDay.length > 0)
                                        ? (metrics as any).apiCostsPerDay.map((d: any) => ({
                                            date: d.date,
                                            openai: d.openaiCost || 0,
                                            gcp: d.gcpCost || 0
                                        }))
                                        : metrics.casesPerDay.map((d: any) => ({
                                            date: d.date,
                                            openai: +(d.count * 0.12).toFixed(2), // Estimated OpenAI cost per case
                                            gcp: +(d.count * 0.03).toFixed(2)     // Estimated GCP cost per case
                                        }))
                                }>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis tickFormatter={(value) => `$${value}`} />
                                    <Tooltip
                                        formatter={(value: any, name: string) => [`$${Number(value).toFixed(2)}`, name]}
                                        labelFormatter={(label) => `Date: ${label}`}
                                    />
                                    <Bar dataKey="openai" stackId="a" fill="#10a37f" name="OpenAI" />
                                    <Bar dataKey="gcp" stackId="a" fill="#4285f4" name="GCP" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>


                    {/* Pie Charts */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ p: 2 }}>
                            <Typography component="h2" variant="h6" color="primary" gutterBottom>Cancer Types</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={metrics.cancerTypeDistribution.filter(d => d.label !== 'Not Provided') as any[]}
                                        dataKey="count"
                                        nameKey="label"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {metrics.cancerTypeDistribution.filter(d => d.label !== 'Not Provided').map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ p: 2 }}>
                            <Typography component="h2" variant="h6" color="primary" gutterBottom>Referral Sources</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={metrics.referralTypeDistribution.filter(d => d.label !== 'Not Provided') as any[]}
                                        dataKey="count"
                                        nameKey="label"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {metrics.referralTypeDistribution.filter(d => d.label !== 'Not Provided').map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ p: 2 }}>
                            <Typography component="h2" variant="h6" color="primary" gutterBottom>Adjudicator Model Choice</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={metrics.modelAdjudicationDistribution as any[]}
                                        dataKey="count"
                                        nameKey="label"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {metrics.modelAdjudicationDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </Box >
        );
    };

    const renderTable = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (view === 'users') {
            const data = users.reduce<(Admin | Provider | Reviewer)[]>((acc, u) => {
                if (userTypeFilter === UserType.ADMIN && u.admin) acc.push(u.admin);
                else if (userTypeFilter === UserType.PROVIDER && u.provider) acc.push(u.provider);
                else if (userTypeFilter === UserType.REVIEWER && u.reviewer) acc.push(u.reviewer);
                return acc;
            }, []);
            if (data.length === 0) return <Typography sx={{ p: 2 }}>No users found.</Typography>;
            if (data.length === 0) return <Typography sx={{ p: 2 }}>No users found.</Typography>;

            // Define columns as { id: string, label: string }
            let columns: { id: string, label: string }[];

            if (userTypeFilter === UserType.PROVIDER) {
                columns = [
                    { id: 'name', label: 'Name' },
                    { id: 'emailAddress', label: 'Email' },
                    { id: 'institutionOrPractice', label: 'Institution' },
                    { id: 'location', label: 'Location' },
                    { id: 'referralType', label: 'Referral' },
                    { id: 'referralName', label: 'Referrer' },
                    { id: 'createdAt', label: 'Signup Time (EST)' },
                    { id: 'caseCount', label: 'Case Count' },
                    { id: 'avgRating', label: 'Average Rating' }
                ];
            } else if (userTypeFilter === UserType.REVIEWER) {
                columns = [
                    { id: 'id', label: 'ID' },
                    { id: 'Full Name', label: 'Full Name' },
                    { id: 'emailAddress', label: 'Email' },
                    { id: 'hospitalOrPractice', label: 'Hospital' },
                    { id: 'specialty', label: 'Specialty' },
                    { id: 'Location', label: 'Location' },
                    { id: 'createdAt', label: 'Created At' },
                    { id: 'isActive', label: 'Active' }
                ];
            } else {
                columns = Object.keys(data[0]).map(key => ({ id: key, label: key }));
            }
            return (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                {columns.map(col => (
                                    <TableCell
                                        key={col.id}
                                        sx={{
                                            fontWeight: 'bold',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((item, index) => (
                                <TableRow key={index}>
                                    {columns.map(col => {
                                        // Handle formatting or virtual columns
                                        let content: React.ReactNode = String(item[col.id as keyof typeof item] || '');

                                        if (col.id === 'registrationComplete') {
                                            const val = item[col.id as keyof typeof item];
                                            content = val ? 'true' : 'false';
                                        }

                                        // Handle provider-specific columns
                                        if (userTypeFilter === UserType.PROVIDER) {
                                            const provider = item as Provider;
                                            if (col.id === 'createdAt' && provider.createdAt) {
                                                content = provider.createdAt.toLocaleString('en-US', {
                                                    timeZone: 'America/New_York',
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                });
                                            } else if (col.id === 'caseCount') {
                                                content = provider.caseCount !== undefined ? provider.caseCount.toString() : '0';
                                            } else if (col.id === 'avgRating') {
                                                content = provider.avgRating !== undefined && provider.avgRating > 0
                                                    ? provider.avgRating.toFixed(1)
                                                    : '-';
                                            } else if (col.id === 'name') {
                                                content = provider.name || '-';
                                            } else if (col.id === 'institutionOrPractice') {
                                                content = provider.institutionOrPractice || '-';
                                            } else if (col.id === 'referralType') {
                                                content = provider.referralType || '-';
                                            } else if (col.id === 'referralName') {
                                                content = provider.referralName || '-';
                                            } else if (col.id === 'location') {
                                                content = provider.location || '-';
                                            }
                                        }

                                        if (userTypeFilter === UserType.REVIEWER) {
                                            const reviewer = item as Reviewer;
                                            if (col.id === 'Full Name') {
                                                content = `${reviewer.firstName} ${reviewer.lastName}`;
                                            } else if (col.id === 'Location') {
                                                content = `${reviewer.city}, ${reviewer.state}`;
                                            } else if (col.id === 'specialty') {
                                                // Map string key back to display label
                                                const specialtyMap: Record<string, string> = {
                                                    'BREAST': 'Breast',
                                                    'THORACIC': 'Thoracic',
                                                    'GI': 'GI',
                                                    'GU': 'GU',
                                                    'GYNO': 'Gyno',
                                                    'HEAD_NECK': 'Head & Neck',
                                                    'NEURO': 'Neuro',
                                                    'SKIN': 'Skin',
                                                    'HEME': 'Heme',
                                                    'OTHER': 'Other'
                                                };
                                                content = specialtyMap[reviewer.specialty] || reviewer.specialty;
                                            }
                                        }

                                        return (
                                            <TableCell
                                                key={col.id}
                                                sx={{
                                                    maxWidth: col.id === 'name' ? 140 : col.id === 'emailAddress' ? 180 : col.id === 'institutionOrPractice' ? 140 : 250,
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'normal',
                                                    verticalAlign: 'top',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                {content}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDeleteUser(item.id, userTypeFilter)}
                                            title="Delete User"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        } else if (view === 'cases') {
            if (cases.length === 0) return <Typography sx={{ p: 2 }}>No cases found.</Typography>;

            // Helper functions to format enum values
            const formatStatus = (status: number) => {
                const statusMap: { [key: number]: string } = {
                    1: 'Processing Input',
                    2: 'Creating Report',
                    3: 'Report Generated',
                    4: 'Generation Failed',
                    5: 'Pending Assignment',
                    6: 'Pending Review',
                    7: 'Review Finalized'
                };
                return statusMap[status] || 'Unknown';
            };

            const formatVerbosity = (verbosity?: number) => {
                if (!verbosity) return '-';
                const verbosityMap: { [key: number]: string } = {
                    1: 'Low',
                    2: 'Medium',
                    3: 'High'
                };
                return verbosityMap[verbosity] || 'Unspecified';
            };

            const formatReasoning = (reasoning?: number) => {
                if (!reasoning) return '-';
                const reasoningMap: { [key: number]: string } = {
                    1: 'Low',
                    2: 'Medium',
                    3: 'High'
                };
                return reasoningMap[reasoning] || 'Unspecified';
            };

            const formatCancerType = (cancerType: number) => {
                const cancerTypeMap: { [key: number]: string } = {
                    1: 'Breast',
                    2: 'Thoracic',
                    3: 'GI',
                    4: 'GU',
                    5: 'Gyno',
                    6: 'Head/Neck',
                    7: 'Neuro',
                    8: 'Skin',
                    9: 'Heme',
                    10: 'Other'
                };
                return cancerTypeMap[cancerType] || 'Not Provided';
            };

            const calculateDuration = (startTime?: Date, endTime?: Date) => {
                if (!startTime || !endTime) return '-';
                const durationMs = endTime.getTime() - startTime.getTime();
                const durationSeconds = Math.round(durationMs / 1000);
                if (durationSeconds < 60) return `${durationSeconds}s`;
                const minutes = Math.floor(durationSeconds / 60);
                const seconds = durationSeconds % 60;
                return `${minutes}m ${seconds}s`;
            };

            const formatSubmitTime = (submitTime?: Date) => {
                if (!submitTime) return '-';
                return submitTime.toLocaleString('en-US', {
                    timeZone: 'America/New_York',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                });
            };

            const getS3Link = (s3Uri: string) => {
                // Convert s3://bucket/key to https://s3.region.amazonaws.com/bucket/key
                // Assuming US West 2 region
                const match = s3Uri.match(/^s3:\/\/([^\/]+)\/(.+)$/);
                if (match) {
                    const bucket = match[1];
                    const key = match[2];
                    return `https://${bucket}.s3.us-west-2.amazonaws.com/${key}`;
                }
                return s3Uri;
            };

            const formatModelDisplay = (model?: string, version?: string) => {
                if (!model && !version) return '-';
                if (model && version) return `${model} (${version})`;
                if (model) return model;
                return version || '-';
            };


            return (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Case ID</TableCell>
                                <TableCell>Provider</TableCell>
                                <TableCell>Cancer Type</TableCell>
                                <TableCell>Submit Time (ET)</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>AI Job Duration</TableCell>
                                <TableCell>Selected Model</TableCell>
                                <TableCell>Rating</TableCell>
                                <TableCell>Human Reviewer</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cases.map((caseItem) => {
                                const newSearch = new URLSearchParams();
                                newSearch.set('case_id', caseItem.id);
                                newSearch.set('from', 'admin');
                                newSearch.set('tab', view);
                                newSearch.set('page', page.toString());
                                newSearch.set('rowsPerPage', rowsPerPage.toString());
                                newSearch.set('userType', userTypeFilter.toString());
                                if (searchParams.has('stagingEnv')) {
                                    newSearch.set('stagingEnv', searchParams.get('stagingEnv')!);
                                }
                                const caseReportUrl = `/case-report?${newSearch.toString()}`;

                                return (
                                    <TableRow
                                        key={caseItem.id}
                                        sx={{
                                            backgroundColor:
                                                caseItem.status === CaseStatus.GENERATION_FAILED ? '#ffebee' :
                                                    caseItem.status === CaseStatus.PENDING_ASSIGNMENT ? '#fffde7' :
                                                        'inherit'
                                        }}
                                    >
                                        <TableCell>{caseItem.id.substring(0, 8)}...</TableCell>
                                        <TableCell>{caseItem.providerName || caseItem.providerId.substring(0, 8) + '...'}</TableCell>
                                        <TableCell>
                                            <Select
                                                value={caseItem.cancerType}
                                                onChange={(e) => handleUpdateCancerType(caseItem.id, e.target.value as CancerType)}
                                                variant="standard"
                                                disableUnderline
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    '& .MuiSelect-select': { py: 0 }
                                                }}
                                            >
                                                {[
                                                    CancerType.NOT_PROVIDED,
                                                    CancerType.BREAST,
                                                    CancerType.THORACIC,
                                                    CancerType.GI,
                                                    CancerType.GU,
                                                    CancerType.GYNO,
                                                    CancerType.HEAD_NECK,
                                                    CancerType.NEURO,
                                                    CancerType.SKIN,
                                                    CancerType.HEME,
                                                    CancerType.OTHER
                                                ].map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {formatCancerType(type)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                        <TableCell>{formatSubmitTime(caseItem.submitTime)}</TableCell>
                                        <TableCell>{formatStatus(caseItem.status)}</TableCell>
                                        <TableCell>{calculateDuration(caseItem.aiJobStartTime, caseItem.aiJobEndTime)}</TableCell>
                                        <TableCell>{caseItem.finalModel || '-'}</TableCell>
                                        <TableCell>
                                            {caseItem.surveyResponse ? (
                                                <MuiTooltip title={caseItem.surveyComment || "No feedback provided"} arrow>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {caseItem.surveyResponse}
                                                        </Typography>
                                                    </Box>
                                                </MuiTooltip>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {(caseItem.status === CaseStatus.PENDING_ASSIGNMENT || caseItem.status === CaseStatus.PENDING_REVIEW) ? (
                                                <Select
                                                    value={caseItem.assignedReviewerId || ''}
                                                    onChange={(e) => handleAssignReviewer(caseItem.id, e.target.value)}
                                                    displayEmpty
                                                    size="small"
                                                    fullWidth
                                                    variant="standard"
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: 1,
                                                        '& .MuiSelect-select': {
                                                            paddingLeft: 1,
                                                            paddingRight: 1,
                                                            paddingTop: 0.5,
                                                            paddingBottom: 0.5
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Unassigned</em>
                                                    </MenuItem>
                                                    {reviewers.map((reviewer) => (
                                                        <MenuItem key={reviewer.id} value={reviewer.id}>
                                                            {reviewer.firstName} {reviewer.lastName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            ) : (caseItem.status === CaseStatus.REVIEW_FINALIZED) ? (
                                                caseItem.assignedReviewerName || '-'
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.0 }}>
                                                <IconButton
                                                    component="a"
                                                    href={caseReportUrl}
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate({
                                                            pathname: '/case-report',
                                                            search: newSearch.toString()
                                                        });
                                                    }}
                                                    title="View Report"
                                                >
                                                    <DescriptionIcon />
                                                </IconButton>
                                                {caseItem.status === CaseStatus.REPORT_GENERATED && (
                                                    <IconButton
                                                        sx={{ color: 'gray' }}
                                                        size="small"
                                                        onClick={() => handleAssignReviewer(caseItem.id, "")}
                                                        title="Assign Reviewer"
                                                    >
                                                        <AssignmentIndIcon />
                                                    </IconButton>
                                                )}
                                                {(caseItem.status === CaseStatus.PENDING_ASSIGNMENT || caseItem.status === CaseStatus.PENDING_REVIEW) && (
                                                    <IconButton
                                                        color="success"
                                                        size="small"
                                                        onClick={() => handleAssignReviewer(caseItem.id, "__RESET_STATUS__")}
                                                        title="Unassign Case (Return to Report Generated)"
                                                    >
                                                        <AssignmentIndIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleDeleteCase(caseItem.id)}
                                                    title="Delete Case"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
        return null;
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e6e6e6' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box
                        component="a"
                        href="/"
                        sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                    >
                        <Box
                            component="img"
                            src="/logo.jpg"
                            alt="Precisca"
                            sx={{
                                height: { xs: 28, sm: 36 },
                                width: 'auto',
                                display: 'block',
                                mr: 1
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            color="inherit"
                            sx={{ color: '#2c5282' }}
                            aria-controls={menuOpen ? 'top-right-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={menuOpen ? 'true' : undefined}
                            onClick={handleMenuOpen}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="top-right-menu"
                            anchorEl={menuAnchorEl}
                            open={menuOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem onClick={handleMenuHome}>Home</MenuItem>
                            <MenuItem onClick={() => { handleMenuClose(); navigate('/register-admin'); }}>Register Admin</MenuItem>
                            <MenuItem onClick={() => { handleMenuClose(); navigate('/register-reviewer'); }}>Register Reviewer</MenuItem>
                            <MenuItem onClick={handleMenuLogout}>Log Out</MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Container maxWidth={false} disableGutters sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ mx: 2 }}>
                    <Tabs value={view} onChange={handleViewChange} indicatorColor="primary" textColor="primary" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tab label="Dashboard" value="dashboard" />
                        <Tab label="Users" value="users" />
                        <Tab label="Cases" value="cases" />
                    </Tabs>

                    {view === 'dashboard' ? (
                        renderDashboard()
                    ) : (
                        <>
                            {/* Top Pagination Bar */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                px: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                                py: 1
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                    <Box sx={{
                                        flexGrow: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        maxWidth: { xs: '100%', md: '40%' }
                                    }}>
                                        {view === 'users' ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <FormControl size="small" sx={{ my: 1, minWidth: 120 }}>
                                                    <InputLabel>User Type</InputLabel>
                                                    <Select value={userTypeFilter} label="User Type" onChange={handleUserTypeChange}>
                                                        <MenuItem value={UserType.PROVIDER}>Providers</MenuItem>
                                                        <MenuItem value={UserType.ADMIN}>Admins</MenuItem>
                                                        <MenuItem value={UserType.REVIEWER}>Reviewers</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                {userTypeFilter === UserType.PROVIDER && (
                                                    <FormControl size="small" sx={{ my: 1, minWidth: 140 }}>
                                                        <InputLabel>Sort By</InputLabel>
                                                        <Select
                                                            value={providerSortBy}
                                                            label="Sort By"
                                                            onChange={(e) => {
                                                                setProviderSortBy(e.target.value);
                                                                setPage(0);
                                                            }}
                                                        >
                                                            <MenuItem value="signup_time">Signup Time</MenuItem>
                                                            <MenuItem value="name">Name</MenuItem>
                                                            <MenuItem value="case_count">Case Count</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <TextField
                                                    size="small"
                                                    placeholder={isMobile ? "Keyword Search" : "Search cases by keyword (e.g. HER2+, etc.)"}
                                                    fullWidth
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            setAppliedSearchQuery(searchQuery);
                                                            setPage(0);
                                                        }
                                                    }}
                                                    sx={{ mr: 1 }}
                                                    slotProps={{
                                                        input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                                                    <SearchIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => {
                                                        setAppliedSearchQuery(searchQuery);
                                                        setPage(0);
                                                    }}
                                                    sx={{ minWidth: { xs: 'auto', sm: '64px' } }}
                                                >
                                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                                        Search
                                                    </Box>
                                                    <Box component="span" sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', alignItems: 'center' }}>
                                                        <SearchIcon />
                                                    </Box>
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                        component="div"
                                        count={totalCount}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        labelRowsPerPage={
                                            <>
                                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Rows per page:</Box>
                                                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Rows:</Box>
                                            </>
                                        }
                                        sx={{
                                            display: { xs: 'none', md: 'flex' },
                                            borderBottom: 'none',
                                            '& .MuiTablePagination-toolbar': {
                                                px: { xs: 0, md: 2 },
                                                flexWrap: { xs: 'wrap', md: 'nowrap' },
                                                justifyContent: { xs: 'center', md: 'flex-end' }
                                            }
                                        }}
                                    />
                                </Box>

                                {(view === 'cases' || (view === 'users' && userTypeFilter === UserType.PROVIDER)) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 2 }}>
                                            Additional Settings:
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                            <Checkbox
                                                checked={hideInternal}
                                                onChange={(e) => {
                                                    setHideInternal(e.target.checked);
                                                    setPage(0);
                                                }}
                                                size="small"
                                                sx={{ p: 0.5 }}
                                            />
                                            <Typography variant="body2" sx={{ cursor: 'pointer' }} onClick={() => {
                                                setHideInternal(!hideInternal);
                                                setPage(0);
                                            }}>
                                                {view === 'cases' ? 'Hide Internal Cases' : 'Hide Internal Providers'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {renderTable()}

                            {/* Bottom Pagination Bar */}
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                component="div"
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={
                                    <>
                                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Rows per page:</Box>
                                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Rows:</Box>
                                    </>
                                }
                            />
                        </>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default AdminPortal;